import CircleXMark from "@dana/Icons/CircleXMark";
import MessageIcon from "@dana/Icons/MessageIcon";
import PlusIcon from "@dana/Icons/PlusIcon";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes } from "@dana/MobiusOverrides/styles/typography";
import { StandardReturnOrderDetails } from "@dana/Services/StandardReturnDetailsService";
import PostInvoicesTypeEmailHandler from "@dana/Store/Data/OrderHistory/Handlers/PostInvoicesTypeEmailHandler";
import PostOrderDetailsEmailHandler from "@dana/Store/Data/OrderHistory/Handlers/PostOrderDetailsEmailHandler";
import PostPackingSlipEmailHandler from "@dana/Store/Data/OrderHistory/Handlers/PostPackingSlipEmailHandler";
import { InvoiceDetailsResponse, OrderDetailEmail, OrderPackingSlip } from "@dana/Types/ApiModels";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { OrderModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { IconMemo, IconProps, IconWrapper } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// --- Props Interface ---
interface InvoiceRecepientsProps {
    invoicetype: string;
    invoiceData: OrderPackingSlip | InvoiceDetailsResponse | OrderDetailEmail | StandardReturnOrderDetails | undefined;
    contentRef?: React.RefObject<HTMLElement | null>;
    onSend?: (payload: { emails: string[]; contentHtml?: string }) => void;
    showText?: boolean;
    textLabel?: string;
    size?: number;
}
const mapStateToProps = (state: ApplicationState) => {
    return {
        userName: state.context.session.userName,
    };
};
const mapDispatchToProps = {
    PostPackingSlipEmailHandler,
    PostInvoicesTypeEmailHandler,
    PostOrderDetailsEmailHandler,
};
type OwnProps = InvoiceRecepientsProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

// --- Styles Interfaces and Objects ---
export interface InvoiceRecepientsStyles {
    messageIcon?: IconProps;
    emailFieldWrapper?: InjectableCss;
    recepientText?: TypographyPresentationProps;
    modalContent?: InjectableCss;
    currentUserCheckBox?: CheckboxPresentationProps;
    addEmailButton?: ButtonPresentationProps;
    removeEmailButton?: ButtonPresentationProps;
}

export const InvoiceRecepientsStyles: InvoiceRecepientsStyles = {
    messageIcon: {
        color: themeColors.primary.danaBlue,
        css: css`
            &:hover {
                svg {
                    path {
                        fill: ${themeColors.secondary.danaDarkBlue};
                    }
                }
            }
        `,
    },
    emailFieldWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 16px;
            margin-bottom: 16px;

            &:first-of-type {
                .remove-button {
                    visibility: hidden;
                }
            }
        `,
    },
    recepientText: {
        size: fontSizes.medium,
        color: themeBackgrounds.darkGray,
        css: css`
            display: block;
            margin-bottom: 8px;
        `,
    },
    currentUserCheckBox: {
        css: css`
            margin-bottom: 16px;

            span[role="checkbox"] + label {
                strong {
                    margin-left: 4px;
                }
            }
        `,
    },
    addEmailButton: {
        css: css`
            width: 30px;
            height: 30px;
            min-height: 30px;
            border-radius: 15px;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 10px;
            margin: 8px 0;

            ${IconWrapper} {
                svg {
                    width: 16px;
                    height: 16px;
                }
            }
        `,
    },
    removeEmailButton: {
        css: css`
            background: transparent;
            padding: 0;
            border: 0;
            min-height: auto;
            height: 30px;
            width: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: ${themeBackgrounds.lightGray};
            border-radius: 50%;
            flex-shrink: 0;

            ${IconWrapper} {
                width: 16px;
                height: 16px;
                svg {
                    width: 16px;
                    height: 16px;
                    path {
                        fill: ${themeColors.primary.richBlack};
                    }
                }
            }
        `,
    },
};

const styles = InvoiceRecepientsStyles;

const MAX_EMAIL_FIELDS = 5;

function collectStyledComponentsCSS(): string {
    const styleEls = Array.from(document.querySelectorAll<HTMLStyleElement>("style[data-styled]"));
    let css = "";
    for (const el of styleEls) {
        const sheet = el.sheet as CSSStyleSheet | null;
        if (!sheet) {
            continue;
        }
        try {
            const rules = Array.from(sheet.cssRules);
            for (const rule of rules) {
                css += `${rule.cssText}\n`;
            }
        } catch (err) {
            // Shouldn’t happen for same-origin inline <style>, but guard anyway
            console.warn("Unable to read styled-components cssRules:", err);
        }
    }
    return css;
}

const InvoiceRecepients: FC<OwnProps> = ({
    userName,
    invoicetype,
    invoiceData,
    PostPackingSlipEmailHandler,
    PostInvoicesTypeEmailHandler,
    PostOrderDetailsEmailHandler,
    contentRef,
    showText = false,
    textLabel = "Email",
    size = 28,
}) => {
    const [availabilityModalIsOpen, setAvailabilityModalIsOpen] = useState<boolean>(false);
    const [emails, setEmails] = useState<string[]>([""]);
    const [emailErrors, setEmailErrors] = useState<string[]>(Array(emails.length).fill(""));
    const toasterContext = React.useContext(ToasterContext);
    const [isemailUser, setIsemailUser] = useState<boolean>(false);

    const [conttent, setConttent] = useState(""); // SSR-safe

    useEffect(() => {
        // Only client-side logic runs here
        if (typeof window === "undefined" || typeof document === "undefined") {
            return;
        }

        let markup = "";
        let target: HTMLElement | null = null;

        if (contentRef?.current) {
            target = contentRef.current;
            markup = target.outerHTML;
        } else {
            const mainElement = document.querySelector("main");
            markup = mainElement?.innerHTML ?? "";
        }

        // Gather style/link markup
        const links = Array.from(document.querySelectorAll("link[rel='stylesheet']"))
            .map(n => n.outerHTML)
            .join("\n");
        // You should provide implementation for collectStyledComponentsCSS
        const scCSS = typeof collectStyledComponentsCSS === "function" ? collectStyledComponentsCSS() : "";

        const docHtml = `<!doctype html>
        <html>
          <head>
            <meta charset="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <title>${document.title}</title>
            ${links}
            <style data-copied-from-styled>${scCSS}</style>
            <style>@media print { button { display: none !important; } }</style>
          </head>
          <body>
            ${markup}
          </body>
        </html>`;

        setConttent(docHtml);
    }, [contentRef]); // Re-runs if ref changes

    const validateEmail = (email: string): string => {
        if (!email.trim()) {
            return translate("Email is required");
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            return translate("Invalid email format");
        }
        return "";
    };

    // Handler for adding a new email field
    const handleAddEmail = () => {
        if (emails.length < MAX_EMAIL_FIELDS) {
            setEmails(prevEmails => [...prevEmails, ""]);
            setEmailErrors(prevErrors => [...prevErrors, ""]); // Add error slot for new field
        } else {
            // eslint-disable-next-line no-alert
            alert(translate(`You can add a maximum of ${MAX_EMAIL_FIELDS} email fields.`));
        }
    };

    // Handler for removing an email field by index
    const handleRemoveEmail = (indexToRemove: number) => {
        if (emails.length > 1) {
            // Ensure at least one field remains
            setEmails(prevEmails => prevEmails.filter((_, index) => index !== indexToRemove));
            setEmailErrors(prevErrors => prevErrors.filter((_, index) => index !== indexToRemove));
        } else {
            // Optionally, prevent removing the last field or clear its value
            setEmails([""]); // Clear the last field if only one remains
            setEmailErrors([""]); // Clear its error
        }
    };

    // Handler for changing an individual email's value
    const handleEmailChange = (index: number, value: string) => {
        const newEmails = [...emails];
        newEmails[index] = value;
        setEmails(newEmails);

        // Re-validate the specific field on change
        const newErrors = [...emailErrors];
        newErrors[index] = validateEmail(value);
        setEmailErrors(newErrors);
    };

    // --- Validation for all emails (e.g., on form submission) ---
    const validateAllEmails = () => {
        let allValid = true;
        const newErrors = emails.map((email, index) => {
            const error = validateEmail(email);
            if (error) {
                allValid = false;
            }
            return error;
        });
        setEmailErrors(newErrors);
        return allValid;
    };

    const handleSendEmail = () => {
        if (validateAllEmails()) {
            const packingHtml = conttent || "";
            if (isemailUser) {
                emails.push(userName);
            }
            if (invoicetype === "order") {
                PostOrderDetailsEmailHandler({
                    recipientEmailAddress: emails.join(","),
                    orderPackingSlipResponse: invoiceData as OrderDetailEmail,
                    packingHtml,
                    invoiceType: invoicetype,
                    onSuccess: () => {
                        setEmails([""]);
                        toasterContext.addToast({ body: siteMessage("TellAFriend_Success"), messageType: "success" });
                        setAvailabilityModalIsOpen(false);
                    },
                    onComplete(resultProps) {
                        if (resultProps.apiResult) {
                            // "this" is targeting the object being created, not the parent SFC
                            // eslint-disable-next-line react/no-this-in-sfc
                        }
                    },
                    onError: (errorResponse: any) => {
                        toasterContext.addToast({
                            body: translate("Error Occured, Please try after some time"),
                            messageType: "danger",
                        });
                    },
                });
            } else if (invoicetype === "slip") {
                PostPackingSlipEmailHandler({
                    recipientEmailAddress: emails.join(","),
                    orderPackingSlipResponse: invoiceData as OrderPackingSlip,
                    packingHtml,
                    onSuccess: () => {
                        setEmails([""]);
                        toasterContext.addToast({ body: siteMessage("TellAFriend_Success"), messageType: "success" });
                        setAvailabilityModalIsOpen(false);
                    },
                    onComplete(resultProps) {
                        if (resultProps.apiResult) {
                            // "this" is targeting the object being created, not the parent SFC
                            // eslint-disable-next-line react/no-this-in-sfc
                        }
                    },
                    onError: (errorResponse: any) => {
                        toasterContext.addToast({
                            body: translate("Error Occured, Please try after some time"),
                            messageType: "danger",
                        });
                    },
                });
            } else if (invoicetype === "StandardReturn") {
                const orderData = invoiceData as StandardReturnOrderDetails;
                PostOrderDetailsEmailHandler({
                    recipientEmailAddress: emails.join(","),
                    orderPackingSlipResponse: {
                        orderNumber: orderData?.processNumber,
                        erpOrderNumber: orderData?.documentNumber,
                        customerPO: orderData?.poNumber,
                        btCompanyName: orderData?.billName,
                        customerNumber: orderData?.billTo,
                        orderType:
                            orderData?.orderType === "ZRET"
                                ? translate("Standard Return")
                                : orderData?.orderType === "ZARN"
                                ? translate("Annual Returns")
                                : orderData?.orderType,
                    } as OrderDetailEmail,
                    packingHtml,
                    invoiceType: invoicetype || orderData?.orderType || "StandardOrder",
                    onSuccess: () => {
                        setEmails([""]);
                        toasterContext.addToast({
                            body: siteMessage("TellAFriend_Success"),
                            messageType: "success",
                        });

                        setAvailabilityModalIsOpen(false);
                    },
                    onComplete(resultProps) {
                        if (resultProps.apiResult) {
                            // "this" is targeting the object being created, not the parent SFC
                            // eslint-disable-next-line react/no-this-in-sfc
                        }
                    },
                    onError: (errorResponse: any) => {
                        toasterContext.addToast({
                            body: translate("Error Occured, Please try after some time"),
                            messageType: "danger",
                        });
                    },
                });
            } else {
                PostInvoicesTypeEmailHandler({
                    recipientEmailAddress: emails.join(","),
                    orderPackingSlipResponse: invoiceData as InvoiceDetailsResponse,
                    packingHtml,
                    invoiceType: invoicetype,
                    onSuccess: () => {
                        setAvailabilityModalIsOpen(false);
                        setEmails([""]);
                        toasterContext.addToast({ body: siteMessage("TellAFriend_Success"), messageType: "success" });
                        setAvailabilityModalIsOpen(false);
                    },
                    onComplete(resultProps) {
                        if (resultProps.apiResult) {
                            // "this" is targeting the object being created, not the parent SFC
                            // eslint-disable-next-line react/no-this-in-sfc
                        }
                    },
                    onError: (errorResponse: any) => {
                        toasterContext.addToast({
                            body: translate("Error Occured, Please try after some time"),
                            messageType: "danger",
                        });
                        setAvailabilityModalIsOpen(false);
                    },
                });
            }
        } else {
            translate("Validation failed. Please check errors.");
        }

        setEmails([""]);
    };

    const modalCloseHandler = () => {
        setAvailabilityModalIsOpen(false);
    };

    return (
        <div>
            <Link
                onClick={() => {
                    setAvailabilityModalIsOpen(true);
                }}
                data-test-selector="share_packingslip_button"
            >
                {showText ? (
                    <Typography>{textLabel}</Typography>
                ) : (
                    <IconMemo size={size} src={MessageIcon} {...styles.messageIcon} />
                )}
            </Link>

            <Modal headline={translate("Share")} isOpen={availabilityModalIsOpen} handleClose={modalCloseHandler}>
                <StyledWrapper {...styles.modalContent}>
                    <Checkbox
                        {...styles.currentUserCheckBox}
                        checked={isemailUser}
                        onChange={(e, value) => setIsemailUser(value)}
                    >
                        {translate("Send me a copy")} <strong> {userName}</strong>
                    </Checkbox>

                    <StyledWrapper className="email-recepients">
                        <Typography {...styles.recepientText}>{translate("Recepients")}</Typography>
                        {emails.map((email, index) => (
                            <StyledWrapper key={index} {...styles.emailFieldWrapper}>
                                {" "}
                                {/* Using index as key for simplicity here, but a unique ID is better if emails can be reordered */}
                                <TextField
                                    name={`email-${index}`}
                                    id={`email-${index}`}
                                    autoComplete={`section-recipient-${index} email`}
                                    value={email}
                                    onChange={e => handleEmailChange(index, e.target.value)}
                                    data-test-selector={`recepientEmail-${index}`}
                                    error={emailErrors[index]}
                                />
                                {/* Only show close button if there's more than one field OR it's not the first field and the list is > 1 */}
                                {emails.length > 1 && (
                                    <Button
                                        onClick={() => handleRemoveEmail(index)}
                                        aria-label={translate("Remove email field")}
                                        {...styles.removeEmailButton}
                                        className="remove-button"
                                    >
                                        <IconMemo size={30} src={CircleXMark} />
                                    </Button>
                                )}
                            </StyledWrapper>
                        ))}

                        {/* Plus button to add new fields, limited to MAX_EMAIL_FIELDS */}
                        {emails.length < MAX_EMAIL_FIELDS && (
                            <Button
                                onClick={handleAddEmail}
                                aria-label={translate("Add new email field")}
                                {...styles.addEmailButton}
                            >
                                <IconMemo size={16} src={PlusIcon} />
                            </Button>
                        )}

                        <Button onClick={event => handleSendEmail()}>{translate("SEND EMAIL")}</Button>
                    </StyledWrapper>
                </StyledWrapper>
            </Modal>
        </div>
    );
};
export default connect(mapStateToProps, mapDispatchToProps)(InvoiceRecepients);
// export default InvoiceRecepients;
